import { Float32BufferAttribute, Vector3 } from 'three';
import _ from 'underscore';

import { isDefined } from '../../../../../../utils/variables';
import { FaceId } from '../../../../../elements/design/design-node/utils/face-node';
import {
  LineSegmentId,
  lineSegmentIsBreakLine,
  lineSegmentIsEdgeLine,
  lineSegmentIsPartOfLine,
  LineSegmentNode,
} from '../../../../../elements/design/design-node/utils/line-segment-node';
import {
  isPointPartOfBreakline,
  isPointPartOfLine,
  PointId,
} from '../../../../../elements/design/design-node/utils/point-node';
import { publish, SubscriptionTopic } from '../../../../../messaging/pubsub';
import { DesignTool } from '../../../../design-tool';
import { PickingFace } from '../../../../picking-face';
import { PickingLine } from '../../../../picking-line';
import { PickingSphere } from '../../../../picking-sphere';
import { listLinesAndPolygonsBySelection } from './multi-select';

export type SelectionResult = {
  points: PointId[];
  lineSegments: LineSegmentId[];
  faces: FaceId[];
};

export function getPolygonVertices(tool: DesignTool, lineIds: LineSegmentId[]): Vector3[][] {
  // Apply point grid based on settings.
  const result = listLinesAndPolygonsBySelection(tool.asset.designNode, lineIds);

  // Extract a flat map of the vertices for each polygon in the result.
  return result
    .filter((x) => x.isPolygon)
    .map((x) => x.lines)
    .map((segments) =>
      getPointIdArray(segments).map((i) => tool.asset.designNode.getVertexByPointId(i, 'world')),
    );
}

/**
 * Finds an index in the values of the item at index 1 that is not included in the item at index 2.
 */
function findUniqueIndex(
  items: LineSegmentNode[],
  index1: number,
  index2: number,
): PointId | undefined {
  const segment1 = items.at(index1);
  const segment2 = items.at(index2);

  return isDefined(segment1) && isDefined(segment2)
    ? segment1.values.find((x) => !segment2.values.includes(x))
    : undefined;
}

/**
 * Converts a group of line segment nodes into a flat point array.
 * For three or more segments it will determine the start and end point based on the nodes alone.
 * If it's only one segment the start point needs to be passed in too in order to accurately
 * determine the direction of the line.
 */
export function getPointIdArray(items: LineSegmentNode[], startPoint?: PointId): PointId[] {
  if (items.length === 0) {
    return [];
  }

  let uniqueItems: PointId[] | undefined = undefined;

  if (items.length === 1) {
    uniqueItems = [...items[0].values];
  } else {
    const firstIndex = findUniqueIndex(items, 0, 1) ?? items.at(0)!.values[0];
    const lastIndex = findUniqueIndex(items, -1, -2) ?? items.at(-1)!.values[0];
    uniqueItems = _.uniq([firstIndex, ...items.flatMap((x) => x.values), lastIndex]);
  }

  if (isDefined(startPoint) && startPoint === uniqueItems.at(-1)) {
    uniqueItems.reverse();
  }

  return uniqueItems;
}

export function deselect(tool: DesignTool) {
  const clearCursor = (cursor: PickingSphere | PickingLine | PickingFace) => {
    cursor.setIndicesAndVertices(new Float32BufferAttribute([], 3), []);
    cursor.visible = false;
  };

  tool.currentSelection.points = [];
  tool.currentSelection.lineSegments = [];
  tool.currentSelection.faces = [];
  clearCursor(tool.selectedPoints);
  clearCursor(tool.selectedLineSegments);
  clearCursor(tool.selectedFaces);
  publish(SubscriptionTopic.DesignToolMultiselectUpdated, undefined);
}

export function selectLines(tool: DesignTool, lines: LineSegmentNode[]) {
  applySelectLineOrPolygon(tool, lines, 'select');
}

export function selectPoints(tool: DesignTool, points: PointId[]) {
  tool.refreshSelectedPointsCursor(points, 'select');
  publish(SubscriptionTopic.DesignToolMultiselectUpdated, undefined);

  tool.selectedPoints.visible = true;
  tool.selectedLineSegments.visible = true;
  tool.selectedFaces.visible = true;
}

export function applySelectLineOrPolygon(
  tool: DesignTool,
  lines: LineSegmentNode[],
  selectionMode: 'select' | 'deselect',
) {
  const points = getPointIdArray(lines);

  tool.refreshSelectedPointsCursor(points, selectionMode);
  tool.refreshSelectedLineSegmentsCursor(
    lines.map((x) => x.id),
    selectionMode,
  );

  tool.selectedPoints.visible = true;
  tool.selectedFaces.visible = true;

  if (!tool.multiSelectInProgress) {
    deselect(tool);
  }
  publish(SubscriptionTopic.DesignToolMultiselectUpdated, undefined);
}

export function applySelectionResult(tool: DesignTool, selection: SelectionResult) {
  tool.refreshSelectedPointsCursor(selection.points);
  tool.refreshSelectedLineSegmentsCursor(selection.lineSegments);
  tool.refreshSelectedFacesCursor(selection.faces);
  if (!tool.multiSelectInProgress) {
    deselect(tool);
  }
  publish(SubscriptionTopic.DesignToolMultiselectUpdated, undefined);
}

/**
 * Gets the multiselect result based on points.
 * If both points in a line segment are included the line segment will be included.
 * If all three points of a face are included the face will be included.
 *
 * The result will be filtered based on the select options in the geometry editor tool.
 */
export function getMultiselectResult(tool: DesignTool, selectedPoints: PointId[]): SelectionResult {
  const lineSegmentIds = tool.asset.designNode.lineSegments;
  const faceIds = tool.asset.designNode.faces;

  const filteredSelection: SelectionResult = {
    points: selectedPoints,
    lineSegments: [],
    faces: [],
  };

  const isPointIncluded = (value: PointId) => filteredSelection.points.includes(value);

  const edgeLines: LineSegmentId[] = [];

  // Populate the selection with lines.
  if (tool.selectOptions.lines || tool.selectOptions.breakLines) {
    for (const id of lineSegmentIds) {
      const line = tool.asset.designNode.getLineSegmentById(id);

      if (isPointIncluded(line.values[0]) && isPointIncluded(line.values[1])) {
        const isBreakline = lineSegmentIsBreakLine(tool.asset.designNode, id);
        const isLine = lineSegmentIsPartOfLine(tool.asset.designNode, id);
        const edgeLine = lineSegmentIsEdgeLine(tool.asset.designNode, id);

        if (edgeLine) {
          edgeLines.push(id);
        }

        if (
          (tool.selectOptions.lines && isLine) ||
          (tool.selectOptions.breakLines && isBreakline) ||
          (tool.selectOptions.faces && tool.selectOptions.lines && edgeLine)
        ) {
          filteredSelection.lineSegments.push(id);
        }
      }
    }
  }

  // Populate the selection with faces.
  if (tool.selectOptions.faces) {
    for (const id of faceIds) {
      const face = tool.asset.designNode.getFaceById(id);

      if (
        isPointIncluded(face.values[0]) &&
        isPointIncluded(face.values[1]) &&
        isPointIncluded(face.values[2])
      ) {
        filteredSelection.faces.push(id);
      }
    }
  }

  // Filter points.
  if (!tool.selectOptions.points) {
    filteredSelection.points = filteredSelection.points.filter(
      (x) => tool.selectOptions.lines && isPointPartOfLine(tool.asset.designNode, x),
    );
  } else {
    if (!tool.selectOptions.lines) {
      filteredSelection.points = filteredSelection.points.filter((x) => {
        const partOfLine = isPointPartOfLine(tool.asset.designNode, x);

        return (
          !partOfLine &&
          !edgeLines.some((segmentId) => {
            return tool.asset.designNode.getLineSegmentById(segmentId).values.includes(x) ?? false;
          })
        );
      });
    }

    if (!tool.selectOptions.faces && !tool.selectOptions.breakLines) {
      filteredSelection.points = filteredSelection.points.filter(
        (x) => !isPointPartOfBreakline(tool.asset.designNode, x),
      );
    }
  }

  return filteredSelection;
}
